import { useState } from "react";
import { Header } from "components";
import { endOfDay, format, isAfter, isValid, startOfDay, subDays } from "date-fns";
import { Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Routes } from "router";
import { DatePicker, Typography } from "theme";

import { Box, Button, TableBody, TableCell, TableRow, useTheme } from "@mui/material";

import { useBonusStatisticsReport } from "../../api/hooks/useBonusStatisticsReport/useBonusStatisticsReport.ts";
import DatepickerPopper from "../../theme/components/DatePicker/DatepickerPopper.tsx";
import {
  StyledTable,
  StyledTableCell,
  StyledTableContainer,
  StyledTableHead,
} from "../../theme/components/Table/Table.tsx";
import { useAuth } from "../../utils/context/Auth/AuthContext.tsx";
import { downloadBlob, readFirstSheet } from "../../utils/spreadsheet.ts";

const REQUEST_DATE_FORMAT = "yyyy-MM-dd'T'HH:mm:ss";

// The pickers report a partly typed date as an Invalid Date.
const isValidDate = (date: Date | null): date is Date => !!date && isValid(date);

const datePickerSlotProps = {
  textField: {
    sx: {
      width: { xs: "100%", sm: "280px" },
      minWidth: "200px",
    },
  },
};

const Bonuses = () => {
  const { user } = useAuth();
  const { palette } = useTheme();
  const [startDate, setStartDate] = useState<Date | null>(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [rows, setRows] = useState<string[][] | null>(null);
  const report = useBonusStatisticsReport();

  if (!user) return null;

  if (user.email !== import.meta.env.VITE_ADMIN_EMAIL) return <Navigate to={Routes.Root} />;

  // Whole days: the pickers keep the current time of day, which would cut off part of both ends.
  const range =
    isValidDate(startDate) && isValidDate(endDate)
      ? { start: startOfDay(startDate), end: endOfDay(endDate) }
      : null;
  const isRangeInvalid = !!range && isAfter(range.start, range.end);
  const canRequest = !!range && !isRangeInvalid && !report.isPending;

  const fetchReport = async () => {
    if (!range) return null;

    try {
      return await report.mutateAsync({
        startDate: format(range.start, REQUEST_DATE_FORMAT),
        endDate: format(range.end, REQUEST_DATE_FORMAT),
      });
    } catch {
      // useBonusStatisticsReport has already shown the error.
      return null;
    }
  };

  const handleViewReport = async () => {
    const blob = await fetchReport();

    if (!blob) return;

    try {
      setRows(await readFirstSheet(blob));
    } catch {
      toast.error("Не вдалося прочитати файл звіту");
    }
  };

  const handleDownloadReport = async () => {
    const blob = await fetchReport();

    if (blob && range) {
      downloadBlob(
        blob,
        `bonuses-${format(range.start, "yyyy-MM-dd")}-to-${format(range.end, "yyyy-MM-dd")}.xlsx`,
      );
    }
  };

  const [header = [], ...body] = rows ?? [];

  const renderContent = () => {
    if (!rows) {
      return (
        <Typography fontSize="18px" textAlign="center">
          Оберіть діапазон дат та натисніть "Переглянути звіт", щоб отримати звіт по бонусах.
        </Typography>
      );
    }

    if (!body.length) {
      return (
        <Typography fontSize="18px" textAlign="center">
          Немає даних за обраний період.
        </Typography>
      );
    }

    return (
      <Box width="100%" maxWidth="100%" overflow="auto">
        <StyledTableContainer
          sx={{ maxHeight: "calc(100vh - 200px)", minWidth: { xs: "100%", sm: "600px" } }}
        >
          <StyledTable stickyHeader>
            <StyledTableHead>
              <TableRow>
                {header.map((cell, index) => (
                  <TableCell
                    key={index}
                    sx={{ whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 2 }}
                  >
                    {cell}
                  </TableCell>
                ))}
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {body.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <StyledTableCell key={cellIndex} sx={{ whiteSpace: "nowrap" }}>
                      {cell}
                    </StyledTableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>
      </Box>
    );
  };

  return (
    <>
      <Header />
      <Box width="100%" height="100vh" display="flex" flexDirection="column" overflow="hidden">
        <Box
          p={2}
          display="flex"
          alignItems="center"
          gap={2}
          flexWrap="wrap"
          bgcolor={palette.primary.main}
          borderBottom={`1px solid ${palette.secondary.light}`}
        >
          <DatePicker
            label="Дата початку"
            value={startDate}
            onChange={(newValue) => setStartDate(newValue)}
            slots={{ popper: DatepickerPopper }}
            slotProps={datePickerSlotProps}
          />
          <DatePicker
            label="Дата закінчення"
            value={endDate}
            onChange={(newValue) => setEndDate(newValue)}
            slots={{ popper: DatepickerPopper }}
            slotProps={datePickerSlotProps}
          />
          <Button
            variant="contained"
            onClick={handleViewReport}
            disabled={!canRequest}
            sx={{ minWidth: "150px" }}
          >
            {report.isPending ? "Завантаження..." : "Переглянути звіт"}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleDownloadReport}
            disabled={!canRequest}
            sx={{ minWidth: "150px" }}
          >
            Завантажити
          </Button>
          {isRangeInvalid && (
            <Typography color="error" variant="body2">
              Дата початку пізніше за дату закінчення
            </Typography>
          )}
        </Box>
        <Box
          flex={1}
          p={2}
          display="flex"
          alignItems={rows && body.length ? "flex-start" : "center"}
          justifyContent="center"
          overflow="auto"
        >
          {renderContent()}
        </Box>
      </Box>
    </>
  );
};

export default Bonuses;
